// Pre-loads the investor deck chunks through the authenticated
// `/v1/investor-deck/:scope` (JS) and `/v1/investor-deck-styles/:scope`
// (CSS) endpoints, *before* the deck's dynamic `import()` runs.
//
// How it works (webpack 4 / Vue CLI 4):
//   1. Fetch the concatenated chunk JS over fetch with a Bearer token.
//   2. Inject the JS as a script element. The chunk's IIFE pushes itself into
//      `window.webpackJsonp` exactly like a normal chunk `<script>` tag would,
//      and webpack's runtime marks the chunkIds as installed.
//   3. The caller then runs the original `import('./InvestorDeepDeck.vue')`.
//      Webpack sees the chunk is already installed and resolves the module
//      from `__webpack_modules__` without ever requesting the chunk URL.
//   4. CSS is fetched in parallel and injected as a `<style>` tag. We also add
//      `data-href` marker tags for the private CSS filenames so webpack's CSS
//      chunk loader treats them as already loaded and skips public `/css/...`
//      requests.
//
// Hosting never publishes the deck artifacts, so an unauthenticated visitor
// has no path to the content even if they discover the route URL.

import { getTernApiBase } from '@/lib/ternApi';

const CSS_HREF_HEADER = 'X-Investor-Deck-Css-Hrefs';

const SCOPES = {
  pitch: { storageKey: 'investor_pitch_auth' },
  onepager: { storageKey: 'investor_onepager_auth' },
};

const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host === '[::1]' ||
    host.endsWith('.local')
  );
};

// Cache the in-flight + completed loads per scope so a re-render or repeated
// route entry doesn't re-fetch and re-eval the same chunks.
const inFlight = new Map();

const getCssHrefs = (response) => {
  const header = response.headers.get(CSS_HREF_HEADER);
  if (!header) return [];

  return header
    .split(',')
    .map((href) => href.trim())
    .filter(Boolean);
};

const getCssHrefVariants = (href) => {
  const trimmed = href.trim();
  if (!trimmed) return [];

  const withoutLeadingSlash = trimmed.replace(/^\/+/, '');
  return Array.from(
    new Set([trimmed, `/${withoutLeadingSlash}`, withoutLeadingSlash]),
  );
};

const hasCssHrefMarker = (href) =>
  Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style[data-href]'),
  ).some(
    (tag) =>
      tag.getAttribute('data-href') === href ||
      tag.getAttribute('href') === href,
  );

const markCssHrefLoaded = (href, scope) => {
  getCssHrefVariants(href).forEach((hrefVariant) => {
    if (hasCssHrefMarker(hrefVariant)) return;

    const marker = document.createElement('style');
    marker.dataset.investorDeck = scope;
    marker.setAttribute('data-href', hrefVariant);
    document.head.appendChild(marker);
  });
};

export default function ensureInvestorDeckLoaded(scope) {
  // Localhost runs the Vue CLI dev server, which serves chunks directly with
  // no auth gate. Skip the override and let webpack's normal `import()` flow
  // do its thing.
  if (isLocalhost()) return Promise.resolve();

  if (inFlight.has(scope)) return inFlight.get(scope);

  const config = SCOPES[scope];
  if (!config) {
    return Promise.reject(new Error(`Unknown investor deck scope: ${scope}`));
  }

  const promise = (async () => {
    const token = window.localStorage.getItem(config.storageKey);
    if (!token) {
      throw new Error('Investor auth token not found.');
    }

    const apiBase = getTernApiBase();
    const headers = { Authorization: `Bearer ${token}` };

    const [jsResp, cssResp] = await Promise.all([
      fetch(`${apiBase}/investor-deck/${scope}`, { headers }),
      fetch(`${apiBase}/investor-deck-styles/${scope}`, { headers }),
    ]);

    if (!jsResp.ok) {
      throw new Error(`investor-deck JS fetch failed: ${jsResp.status}`);
    }
    const js = await jsResp.text();

    // The chunk JS is a webpack JSONP push — running it registers the
    // chunk's modules and marks the chunkIds installed. We inject a
    // <script> element rather than using `new Function` because iOS
    // WebKit treats Function-constructor execution under stricter rules
    // in some contexts, and script-element injection always runs in
    // the standard script execution path that's never blocked.
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.dataset.investorDeck = scope;
      script.textContent = js;
      // Synchronous text-content scripts execute on append, so we listen
      // for an immediate error event and resolve on the next microtask.
      const onError = () => {
        reject(new Error(`investor-deck script execution failed (${scope})`));
      };
      script.addEventListener('error', onError, { once: true });
      try {
        document.head.appendChild(script);
        // appendChild executed the script synchronously by the time we
        // get here. If it threw synchronously, the catch below handles it.
        resolve();
      } catch (err) {
        reject(err);
      }
    });

    if (cssResp.ok) {
      const cssHrefs = getCssHrefs(cssResp);
      const css = await cssResp.text();
      if (css.trim().length > 0) {
        const style = document.createElement('style');
        style.dataset.investorDeck = scope;
        style.textContent = css;
        document.head.appendChild(style);
      }
      cssHrefs.forEach((href) => markCssHrefLoaded(href, scope));
    }
  })();

  inFlight.set(scope, promise);
  // If the load fails, drop the cached promise so the next attempt can retry
  // (e.g. after the user re-authenticates).
  promise.catch(() => inFlight.delete(scope));
  return promise;
}
